import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Search, FileText, TrendingUp, Zap, X } from "lucide-react";
import { supabase } from "../../lib/supabase";
import RoleBasedHeader from "../../components/ui/RoleBasedHeader";
import StatTile from "./components/StatTile";
import DateRangeFilter from "./components/DateRangeFilter";
import SubmissionCard from "./components/SubmissionCard";
import EditResubmitModal from "./components/EditResubmitModal";

// Maps a real mill_price_quotes status value to the tab key this page uses for display.
const STATUS_TO_TAB = { pending_approval: "pending", rejected: "rejected", live: "live" };

// Cannot be fully tested end-to-end until mill_price_quotes has real rows for the signed-in officer
// (currently 0 rows in the table) — every tab will render its genuine empty state until an officer
// actually submits something. Query is structurally verified (schema, FK, RLS) but not click-tested.
//
// select q.*, m.canonical_name as mill_name, m.source_state, m.source_district
// from mill_price_quotes q
// join mi_mills m on m.id = q.mill_id
// where q.submitted_by = auth.uid()
// order by q.submitted_at desc;
const fetchMySubmissions = async () => {
  const { data: { user } } = await supabase?.auth?.getUser();
  if (!user) return { rows: [], error: "Not signed in" };

  const { data, error } = await supabase
    ?.from("mill_price_quotes")
    ?.select("*, mi_mills!inner(canonical_name, source_state, source_district)")
    ?.eq("submitted_by", user?.id)
    ?.order("submitted_at", { ascending: false });

  if (error) return { rows: [], error: error?.message };

  // A row is "expired" when it's still live, still has stock, but its lifting date
  // has passed with nobody buying it — it needs a human decision (bump the date and
  // resubmit, or leave it as expired). It stops needing attention once a NEWER
  // submission for the same mill+grade is itself live or pending_approval — i.e.
  // someone already acted on it. A newer submission that got rejected does NOT
  // count as "handled": the original problem is still unsolved, so the expired row
  // stays flagged. (Confirmed with KK.)
  const todayStr = new Date()?.toISOString()?.slice(0, 10);
  // Treat a missing submitted_at as "very old" rather than letting the raw comparison
  // silently no-op both directions (JS coerces a null/string comparison to NaN, which
  // is never > or < anything) — found during a recheck pass, hardened defensively.
  const safeSubmittedAt = (row) => row?.submitted_at || '0001-01-01';
  const isSuperseded = (row) =>
    (data || [])?.some((other) =>
      other?.id !== row?.id &&
      other?.mill_id === row?.mill_id &&
      other?.grade === row?.grade &&
      safeSubmittedAt(other) > safeSubmittedAt(row) &&
      (other?.status === 'live' || other?.status === 'pending_approval')
    );

  const rows = (data || [])
    ?.filter((row) => STATUS_TO_TAB?.[row?.status]) // only pending_approval/rejected/live — 'draft' rows aren't shown here
    ?.filter((row) => row?.status !== 'live' || Number(row?.available_qty) > 0) // exhausted live rows (booked out to 0) vanish entirely, not just show 0
    ?.map((row) => {
      const isExpired =
        row?.status === 'live' &&
        Number(row?.available_qty) > 0 &&
        row?.lifting_date &&
        row?.lifting_date < todayStr &&
        !isSuperseded(row);
      return {
        id: row?.id,
        millId: row?.mill_id,
        millName: row?.mi_mills?.canonical_name,
        sourceState: row?.mi_mills?.source_state,
        sourceDistrict: row?.mi_mills?.source_district,
        grade: row?.grade,
        season: row?.season,
        rateExcl: row?.rate_excl_gst,
        rateIncl: row?.rate_incl_gst,
        qty: row?.available_qty,
        paymentDate: row?.payment_date,
        liftingDate: row?.lifting_date,
        status: STATUS_TO_TAB?.[row?.status],
        isExpired,
        submittedAt: row?.submitted_at,
        approvedAt: row?.approved_at,
        // No display-name join was specified for the approver — approved_by is a bare auth.users uuid.
        // approved_by_role (e.g. "quota_manager") is the only human-readable field available without
        // adding a join the task didn't ask for.
        approvedBy: row?.approved_by_role || null,
        rejectionReason: row?.rejection_reason,
      };
    });

  return { rows, error: null };
};

// Unscoped on purpose — no .eq("submitted_by", ...). An expired offer needs fixing
// by whoever's available, not just the officer who originally submitted it, so this
// intentionally sees every officer's rows RLS allows: the signed-in officer's own
// (any status) plus everyone's 'live' rows (mpq_public_select_live). It does NOT see
// another officer's still-pending resubmission — RLS has no policy for that — so the
// supersession check here is slightly less complete than admin's Submission History,
// but only in the safe direction (can still flag something someone's mid-fixing,
// never hides something that actually needs attention).
const fetchExpiredSubmissions = async () => {
  // No .eq("status", "live") here — the supersession check below needs to see a
  // fresh resubmission even while it's still 'pending_approval' (including the
  // signed-in officer's own), or a same-day fix would keep showing as expired
  // until approval finished. RLS still correctly scopes what comes back: the
  // signed-in officer's own rows (any status) plus everyone's 'live' rows.
  const { data, error } = await supabase
    ?.from("mill_price_quotes")
    ?.select("*, mi_mills!inner(canonical_name, source_state, source_district)")
    ?.order("submitted_at", { ascending: false });

  if (error) return { rows: [], error: error?.message };

  const todayStr = new Date()?.toISOString()?.slice(0, 10);
  const safeSubmittedAt = (row) => row?.submitted_at || '0001-01-01';
  const isSuperseded = (row) =>
    (data || [])?.some((other) =>
      other?.id !== row?.id &&
      other?.mill_id === row?.mill_id &&
      other?.grade === row?.grade &&
      safeSubmittedAt(other) > safeSubmittedAt(row) &&
      (other?.status === 'live' || other?.status === 'pending_approval')
    );

  const rows = (data || [])
    ?.filter((row) =>
      row?.status === 'live' &&
      Number(row?.available_qty) > 0 &&
      row?.lifting_date &&
      row?.lifting_date < todayStr &&
      !isSuperseded(row)
    )
    ?.map((row) => ({
      id: row?.id,
      millId: row?.mill_id,
      millName: row?.mi_mills?.canonical_name,
      sourceState: row?.mi_mills?.source_state,
      sourceDistrict: row?.mi_mills?.source_district,
      grade: row?.grade,
      season: row?.season,
      rateExcl: row?.rate_excl_gst,
      rateIncl: row?.rate_incl_gst,
      qty: row?.available_qty,
      paymentDate: row?.payment_date,
      liftingDate: row?.lifting_date,
      status: "live",
      isExpired: true,
      submittedAt: row?.submitted_at,
      approvedAt: row?.approved_at,
      approvedBy: row?.approved_by_role || null,
      rejectionReason: row?.rejection_reason,
    }));

  return { rows, error: null };
};

const TABS = [
  { key: "all", label: "All" },
  { key: "live", label: "Live" },
  { key: "pending", label: "Pending" },
  { key: "rejected", label: "Rejected" },
  { key: "expired", label: "Expired" },
];

const EMPTY_MESSAGES = {
  all: "You haven't submitted any rates yet.",
  pending: "No quotes awaiting approval right now.",
  rejected: "Nothing rejected — nice work.",
  live: "No approved quotes yet.",
  expired: "Nothing's expired right now.",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const parseDate = (str) => (str ? new Date(str) : null);

const isInRange = (dateStr, from, to) => {
  const d = parseDate(dateStr);
  if (!d) return false;
  const f = from ? new Date(from) : null;
  const t = to ? new Date(to + "T23:59:59") : null;
  if (f && d < f) return false;
  if (t && d > t) return false;
  return true;
};

const formatDisplayDate = (str) => {
  if (!str) return "";
  const d = new Date(str);
  return d?.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

// ─── Main Component ───────────────────────────────────────────────────────────
const MySubmissions = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [editSubmission, setEditSubmission] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [expiredRows, setExpiredRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  const loadSubmissions = useCallback(async () => {
    setLoading(true);
    const [{ rows, error }, { rows: expRows, error: expError }] = await Promise.all([
      fetchMySubmissions(),
      fetchExpiredSubmissions(),
    ]);
    setSubmissions(rows);
    setExpiredRows(expRows);
    setFetchError(error || expError);
    setLoading(false);
  }, []);

  useEffect(() => {
    loadSubmissions();
  }, [loadSubmissions]);

  const hasDateRange = fromDate || toDate;

  // Filter by tab status ("all" passes everything through). "expired" is a special
  // case — it's a derived flag (isExpired), not a real status value, since an expired
  // row's underlying status is still "live".
  const byTab = useMemo(() => {
    if (activeTab === "all") return submissions;
    if (activeTab === "expired") return expiredRows; // unscoped — see fetchExpiredSubmissions
    return submissions?.filter((s) => s?.status === activeTab);
  }, [submissions, expiredRows, activeTab]);

  // Apply search + date filter
  const filtered = useMemo(() => {
    let result = byTab;

    // Search
    if (searchQuery?.trim()) {
      const q = searchQuery?.toLowerCase();
      result = result?.filter((s) => s?.millName?.toLowerCase()?.includes(q));
    }

    // Date range — use each row's own approvedAt if it's live, submittedAt otherwise
    // (matters most in the "All" tab, where rows of every status are mixed together).
    if (hasDateRange) {
      result = result?.filter((s) => {
        const dateField = s?.status === "live" ? s?.approvedAt : s?.submittedAt;
        return isInRange(dateField, fromDate, toDate);
      });
    }

    return result;
  }, [byTab, searchQuery, fromDate, toDate, hasDateRange]);

  // Tab counts (unfiltered by date, only by search)
  const tabCounts = useMemo(() => {
    const counts = {};
    TABS?.forEach(({ key }) => {
      let items = key === "all"
        ? submissions
        : key === "expired"
          ? expiredRows
          : submissions?.filter((s) => s?.status === key);
      if (searchQuery?.trim()) {
        const q = searchQuery?.toLowerCase();
        items = items?.filter((s) => s?.millName?.toLowerCase()?.includes(q));
      }
      counts[key] = items?.length;
    });
    return counts;
  }, [submissions, expiredRows, searchQuery]);

  // Summary stats (all data, no filters)
  const totalSubmitted = submissions?.length;
  const liveCount = submissions?.filter((s) => s?.status === "live")?.length;
  const rejectedCount = submissions?.filter((s) => s?.status === "rejected")?.length;
  const decidedCount = liveCount + rejectedCount;
  const approvalRate = decidedCount > 0 ? Math.round((liveCount / decidedCount) * 100) : 0;

  const handleClearDates = () => {
    setFromDate("");
    setToDate("");
  };

  // INSERT a new mill_price_quotes row copying mill_id from the original + the edited field values,
  // status='pending_approval', submitted_by=auth.uid(), submitted_at=now(). Never touches/overwrites
  // the original rejected row — resubmission is always a fresh row.
  const handleResubmit = async (formData) => {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) return { error: "Not signed in — cannot resubmit" };

    const { error } = await supabase?.from("mill_price_quotes")?.insert({
      mill_id: editSubmission?.millId,
      season: formData?.season,
      grade: formData?.grade,
      rate_excl_gst: Number(formData?.rateExcl),
      payment_date: formData?.paymentDate,
      lifting_date: formData?.liftingDate,
      available_qty: Number(formData?.qty),
      status: "pending_approval",
      submitted_by: user?.id,
      submitted_at: new Date()?.toISOString(),
    });

    if (error) return { error: error?.message };

    await loadSubmissions();
    setActiveTab("pending");
    return {};
  };

  // Withdraws (soft-deletes) only this mill_price_quotes row (the offer) — never
  // mi_mills/factories, and never a hard delete: the row stays in the table with
  // status='withdrawn' so it's still fully visible in Submission History (the
  // permanent audit trail), just no longer live on Mandi/WhatsApp. RLS
  // (mpq_officer_withdraw_own) allows any factory_relations_officer to withdraw
  // any live row — deliberately unscoped, same as this page's own Expired tab
  // fetch (fetchExpiredSubmissions above) — and a DB trigger
  // (cleanup_quotas_on_live_quote_withdrawn) keeps the synced quotas row — what
  // WhatsApp booking reads — in step. See migrations
  // 20260902231636_officer_withdraw_own_quote_instead_of_delete.sql and
  // 20260902235000_widen_officer_withdraw_to_any_live_row.sql.
  //
  // Explicitly checks the returned row count — a blocked RLS write returns no
  // error, just 0 rows affected, which would otherwise silently look like the
  // button did nothing (found in production: a row with no recorded
  // submitted_by fell through the old submitter-scoped policy this way).
  const handleWithdraw = async (submission) => {
    const confirmed = window.confirm(
      `Withdraw this offer for ${submission?.millName}? This only removes the offer — the mill itself stays in the system.`
    );
    if (!confirmed) return;

    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) {
      window.alert("Not signed in — cannot withdraw");
      return;
    }

    const { data, error } = await supabase
      ?.from("mill_price_quotes")
      ?.update({
        status: "withdrawn",
        withdrawn_by: user?.id,
        withdrawn_at: new Date()?.toISOString(),
      })
      ?.eq("id", submission?.id)
      ?.eq("status", "live")
      ?.select("id");
    if (error) {
      window.alert(`Couldn't withdraw: ${error?.message}`);
      return;
    }
    if (!data || data?.length === 0) {
      window.alert("Couldn't withdraw — this offer may have already been updated by someone else. Refreshing the list.");
      await loadSubmissions();
      return;
    }
    await loadSubmissions();
  };

  const isDateFiltered = hasDateRange && filtered?.length === 0;
  const isGenuinelyEmpty = !hasDateRange && filtered?.length === 0;

  return (
    <div className="min-h-screen" style={{ background: "#faf7f2", fontFamily: "'DM Sans', sans-serif" }}>
      <Helmet>
        <title>My Submissions – SugarHathi</title>
        <meta name="description" content="Track the status of your submitted mill rates — pending, live, or rejected." />
        <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
      </Helmet>
      <RoleBasedHeader />
      <div className="max-w-4xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        {/* ── Top Bar ── */}
        <div className="mb-5">
          <h1 className="text-2xl sm:text-3xl font-extrabold mb-1" style={{ color: "#16354f" }}>
            My Submissions
          </h1>
          <p className="text-sm mb-4" style={{ color: "#52606d" }}>
            Every rate you submit appears below the moment it's approved — or rejected.
          </p>

          {/* Search + Date Filters */}
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Search */}
            <div className="relative flex-1">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
              />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                placeholder="Search by mill name…"
                className="w-full pl-9 pr-4 py-2.5 text-sm rounded-xl bg-white text-[#1f2937] placeholder-gray-400 focus:outline-none focus:ring-2 shadow-sm"
                style={{ border: "1px solid #f3e8d0" }}
              />
            </div>

            {/* Date Range */}
            <DateRangeFilter
              fromDate={fromDate}
              toDate={toDate}
              onFromChange={setFromDate}
              onToChange={setToDate}
              onClear={handleClearDates}
            />
          </div>
        </div>

        {/* ── Summary Strip ── */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
          <StatTile
            icon={FileText}
            label="Total Submitted"
            value={totalSubmitted}
            style={{ background: "linear-gradient(135deg, #1e4a72, #16354f)" }}
          />
          <StatTile
            icon={TrendingUp}
            label="Approval Rate"
            value={`${approvalRate}%`}
            style={{ background: "linear-gradient(135deg, #d97706, #92400e)" }}
          />
          <StatTile
            icon={Zap}
            label="Currently Live"
            value={liveCount}
            style={{ background: "linear-gradient(135deg, #0f766e, #0b5c54)" }}
          />
        </div>

        {/* ── Status filter chips ── */}
        <div className="mb-1">
          <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1 flex-wrap">
            {TABS?.map(({ key, label }) => {
              const isActive = activeTab === key;
              return (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  className="flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-colors duration-150"
                  style={
                    isActive
                      ? { background: "#16354f", color: "#fff", border: "1px solid #16354f" }
                      : { background: "#fff", color: "#52606d", border: "1px solid #f3e8d0" }
                  }
                >
                  {label}
                  <span
                    className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold"
                    style={
                      isActive
                        ? { background: "rgba(255,255,255,0.22)", color: "#fff" }
                        : { background: "#f3e8d0", color: "#92400e" }
                    }
                  >
                    {tabCounts?.[key]}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Date range context line */}
        {hasDateRange && (
          <p className="text-xs text-gray-500 mt-2 mb-3">
            Showing results from{" "}
            <span className="font-semibold text-[#1f2937]">
              {fromDate ? formatDisplayDate(fromDate) : "—"}
            </span>{" "}
            to{" "}
            <span className="font-semibold text-[#1f2937]">
              {toDate ? formatDisplayDate(toDate) : "—"}
            </span>
          </p>
        )}

        {/* ── Content ── */}
        <div className="mt-4 space-y-3">
          {/* Loading state — initial fetch in flight */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-8 h-8 rounded-full border-2 border-amber-200 border-t-amber-500 animate-spin mb-3" />
              <p className="text-sm text-gray-500">Loading your submissions…</p>
            </div>
          )}

          {/* Fetch error — e.g. not signed in, or RLS denied */}
          {!loading && fetchError && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-14 h-14 rounded-full bg-red-50 border border-red-200 flex items-center justify-center mb-4">
                <X size={24} className="text-[#DC143C]" />
              </div>
              <p className="text-base font-semibold text-[#1f2937] mb-1">Couldn't load your submissions</p>
              <p className="text-sm text-gray-500">{fetchError}</p>
            </div>
          )}

          {/* Empty state — date filtered */}
          {!loading && !fetchError && isDateFiltered && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-14 h-14 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center mb-4">
                <Search size={24} className="text-amber-400" />
              </div>
              <p className="text-base font-semibold text-[#1f2937] mb-1">
                No submissions in this date range.
              </p>
              <p className="text-sm text-gray-500 mb-4">
                Try a wider range or clear the filter.
              </p>
              <button
                onClick={handleClearDates}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white rounded-lg"
                style={{ background: "#f59e0b" }}
              >
                <X size={14} />
                Clear dates
              </button>
            </div>
          )}

          {/* Empty state — genuinely empty (this is expected/correct for a real officer with 0 rows) */}
          {!loading && !fetchError && isGenuinelyEmpty && !searchQuery && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-14 h-14 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center mb-4">
                <FileText size={24} className="text-amber-400" />
              </div>
              <p className="text-base font-semibold text-[#1f2937]">
                {EMPTY_MESSAGES?.[activeTab]}
              </p>
            </div>
          )}

          {/* Empty state — search returned nothing */}
          {!loading && !fetchError && filtered?.length === 0 && searchQuery && !hasDateRange && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                <Search size={24} className="text-gray-400" />
              </div>
              <p className="text-base font-semibold text-[#1f2937]">
                No mills match "{searchQuery}"
              </p>
            </div>
          )}

          {/* Cards */}
          {!loading && !fetchError && filtered?.map((submission) => (
            <SubmissionCard
              key={submission?.id}
              submission={submission}
              onEditResubmit={setEditSubmission}
              onUpdateAndResubmit={(s) => navigate(`/factory-rate-intelligence-center?millId=${s?.millId}`)}
              onWithdraw={handleWithdraw}
            />
          ))}
        </div>
      </div>
      {/* Edit & Resubmit Modal */}
      {editSubmission && (
        <EditResubmitModal
          submission={editSubmission}
          onClose={() => setEditSubmission(null)}
          onSubmit={handleResubmit}
        />
      )}
    </div>
  );
};

export default MySubmissions;
